import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Tag, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import useTeacher from "../hooks/useTeacher";
import Datatable from "components/Common/dataTable";
import { useTranslation } from "react-i18next";
import CustomButton from "components/Common/ui/CustomButton";
import { all_routes } from "router/all_routes";
import { useAbility } from "config/abilityContext";
import { getSubjectLabelFromCode , getSubjectCodeFromkey } from "utils/schoolUtils";

type Subject = {
    subjectId: string;
    subjectName: string;
    subjectCode: string;
    colorCode?: string | null;
};

type RawClass = {
    id?: string;
    classId?: string;
    name?: string;
    className?: string;
    subjects?: Subject[];
};



const TeacherClassesTab: React.FC = () => {
    const { id  } = useParams<{ id: string }>();
    const { fetchClassesTeacher, classes, loading } = useTeacher();
    const { t } = useTranslation();
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
    const ability = useAbility();

    useEffect(() => {
        if (id) fetchClassesTeacher(id);
    }, [id]);

    const data: RawClass[] = useMemo(() => {
        const list = Array.isArray(classes) ? classes : [];
        return list.map((c) => ({
            id: c.id,
            classId: c.classId,
            name: c.name,
            className: c.className,
            subjects: Array.isArray(c.subjects) ? c.subjects : [],
        }));
    }, [classes]);

    const columns: ColumnsType<RawClass> = [
        {
            title: t("class"),
            dataIndex: "className",
            key: "className",
            render: (_: any, row) => <strong>{row.className || row.name || "—"}</strong>,
        },
        {
            title: t("subjects"),
            dataIndex: "subjects",
            key: "count",
            align: "center",
            render: (subs?: Subject[]) => subs?.length ?? 0,
            sorter: (a, b) => (a?.subjects?.length ?? 0) - (b?.subjects?.length ?? 0),
        },
        {
            title: t("codes"),
            key: "codes",
            render: (_: any, row) => (
                <Space wrap size={[6, 6]}>
                    {(row.subjects || []).map((s) => {
                        const bg = s.colorCode || "#f5f5f5";
                        return (
                            <Tag
                                key={s.subjectId}
                                style={{
                                    marginInlineEnd: 0,
                                    background: bg,
                                    color: "#1f1f1f",
                                    borderColor: "transparent",
                                    borderRadius: 999,
                                    fontWeight: 600,
                                }}
                                title={s.subjectName}
                            >
                                {getSubjectCodeFromkey(s.subjectCode)}
                            </Tag>
                        );
                    })}
                    {/* {row.subjects && row.subjects.length > 6 && (
                        <Tag>+{row.subjects.length - 6}</Tag>
                    )} */}
                </Space>
            ),
        },
        {
            title: t("actions"),
            key: "actions",
            render: (_: string, record: any) => {
                if (!ability.can("update", "Teacher")) return null;
                return <CustomButton asLink to={all_routes.editTeacher.replace(":id", id ?? "")}>
                    <i className="ti ti-edit-circle me-2"></i>
                    {t("edit")}
                </CustomButton>

            },
        },
    ];

    // const expandedRowRender = (row: RawClass) => {
    //     const subjectCols: ColumnsType<Subject> = [
    //         { title: t("name"), dataIndex: "subjectName", key: "name" , render: (text: string, record: Subject) => {
    //             return getSubjectLabelFromCode(record?.subjectCode );
    //         } },
    //         {
    //             title: t("code"),
    //             dataIndex: "subjectCode",
    //             key: "code",
    //             render: (code: string, s) => {
    //                 const bg = s.colorCode || "#f5f5f5";
    //                 const color = "#1f1f1f";
    //                 return (
    //                     <Tag
    //                         style={{
    //                             background: bg,
    //                             color,
    //                             borderColor: "transparent",
    //                             borderRadius: 999,
    //                             fontWeight: 600,
    //                         }}
    //                     >
    //                         {getSubjectCodeFromkey(code)}
    //                     </Tag>
    //                 );
    //             },
    //         },
    //     ];

    //     return (
    //         <Datatable
    //             columns={subjectCols}
    //             dataSource={row.subjects || []}
    //             rowKey={(s) => s.subjectId}          // ✅ unique key per subject
    //             pagination={false}
    //         />
    //     );
    // };

    return (
        <div className="p-2">
            <Datatable
                columns={columns}
                dataSource={data}
                rowKey={(r: RawClass) => r.id || r.classId || ""}
                loading={loading}
                // expandable={{
                //     expandedRowRender,
                //     expandRowByClick: true,
                //     columnWidth: 48,
                //     expandedRowKeys,
                //     onExpand: (expanded, record) => {
                //         const key = record.id || record.classId || "";
                //         setExpandedRowKeys(expanded ? [key] : []);   // ✅ only one open
                //     },
                // }}
                pagination
            />
        </div>
    );
};

export default TeacherClassesTab;
